/**
 * "Alert me" on the stock Dividend tab: five switches and a delivery channel, saved as one dividend
 * alert subscription for the listing (`alerts/subscriptions`, TED #2449, on the standard alert
 * engine, listed on /alerts with `kind: "dividend"`).
 *
 * A locked caller sees the switches; pressing one opens the upgrade dialog and no alert call is made.
 * The box hides itself when the alert routes are not deployed (`missing`).
 */
import { FC, useEffect, useMemo, useState } from "react";
import DeliverySelect from "@/components/notifications/DeliverySelect";
import { useRouter } from "next/router";
import { deleteDivAlert, getDivAlertSubscriptions, createDivAlert, updateDivAlert } from "@/controllers/dividends-v2";
import { Box } from "./Boxes";
import { DivAlertChannel, DivAlertParams, DivAlertSubscription, DivAlertTrigger } from "@/models/dividends-v2";
import styles from "./StockDividendTab.module.scss";

export const ALERT_PARAMS: DivAlertParams = { move_pct: 5, ex_days: 2, cut_threshold: 0.25 };

export const ALERT_SWITCHES: { key: DivAlertTrigger; label: string }[] = [
    { key: "declared", label: "Declaration, and how it compares with the forecast" },
    // One trigger in TED: fires when the forecast amount moves by more than move_pct or the chance of a
    // cut or omission crosses cut_threshold (ted!3727, domain/dividends/engine_alerts.py).
    { key: "forecast_move", label: `Forecast changes: amount moves more than ${ALERT_PARAMS.move_pct}% or the chance of a cut crosses ${Math.round((ALERT_PARAMS.cut_threshold ?? 0.25) * 100)}%` },
    { key: "late", label: "Declaration running late" },
    { key: "ex_date", label: `Ex-date reminder, ${ALERT_PARAMS.ex_days} days before` },
    { key: "special", label: "Special dividend likely or declared" },
];

/** What a new subscription starts with, as in the mockup: the declaration and forecast moves. */
const DEFAULT_ON: DivAlertTrigger[] = ["declared", "forecast_move"];

/**
 * Before this listing's forecasts are published (TED #2467) only these fire with content: a
 * declaration (without forecast wording), the ex-date reminder and a declared special. A forecast
 * move or a late declaration needs the forecast, so those switches are not offered; a saved
 * subscription keeps them.
 */
const DECLARED_SWITCHES: { key: DivAlertTrigger; label: string }[] = [
    { key: "declared", label: "Dividend declared" },
    { key: "ex_date", label: `Ex-date reminder, ${ALERT_PARAMS.ex_days} days before` },
    { key: "special", label: "Special dividend declared" },
];
const DECLARED_DEFAULT_ON: DivAlertTrigger[] = ["declared", "ex_date"];

type Status = "loading" | "ready" | "missing" | "upgrade" | "error";

const sameSet = (a: DivAlertTrigger[], b: DivAlertTrigger[]) => a.length === b.length && a.every((x) => b.includes(x));

/**
 * Whether to show the box at all: everywhere except where the alert routes answer "not deployed".
 * Locked and demo callers always see it (locked: the upsell; demo: the design).
 */
export const DividendAlertBox: FC<{ tradingitemId: number; ticker: string; locked: boolean; demo: boolean; forecasts?: boolean; onUnlock: () => void; onUnavailable: () => void }> = ({
    tradingitemId,
    ticker,
    locked,
    demo,
    forecasts = true,
    onUnlock,
    onUnavailable,
}) => {
    const router = useRouter();
    const onAlertsPage = router.pathname.startsWith("/alerts");
    const usable = !locked && !demo;
    const switches = forecasts ? ALERT_SWITCHES : DECLARED_SWITCHES;
    const [status, setStatus] = useState<Status>(usable ? "loading" : "ready");
    const [sub, setSub] = useState<DivAlertSubscription | null>(null);
    const [on, setOn] = useState<DivAlertTrigger[]>(forecasts ? DEFAULT_ON : DECLARED_DEFAULT_ON);
    const [channel, setChannel] = useState<DivAlertChannel>("both");
    const [saving, setSaving] = useState(false);
    const [message, setMessage] = useState<string | null>(null);

    useEffect(() => {
        if (!usable) return undefined;
        const ac = new AbortController();
        setStatus("loading");
        getDivAlertSubscriptions(tradingitemId, ac.signal)
            .then((res) => {
                if (res.kind === "ok") {
                    const found = res.data.find((s) => s.tradingitem_id === tradingitemId && s.active !== false) ?? null;
                    setSub(found);
                    if (found) {
                        setOn(found.triggers.filter((t) => switches.some((s) => s.key === t)));
                        setChannel(found.channel);
                    }
                    setStatus("ready");
                } else if (res.kind === "missing") {
                    setStatus("missing");
                    onUnavailable();
                } else setStatus(res.kind === "upgrade" ? "upgrade" : "error");
            })
            .catch(() => undefined);
        return () => ac.abort();
        // onUnavailable is a stable setter from the page.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [usable, tradingitemId]);

    const showLocked = locked || status === "upgrade";
    // Triggers saved earlier that this view does not offer (forecast ones, before publishing) are kept.
    const kept = useMemo(() => (sub ? sub.triggers.filter((t) => !switches.some((s) => s.key === t)) : []), [sub, switches]);
    const dirty = useMemo(() => (sub ? !sameSet([...on, ...kept], sub.triggers) || channel !== sub.channel : on.length > 0), [sub, on, kept, channel]);
    const disabled = !showLocked && (demo || status !== "ready" || saving);

    const toggle = (key: DivAlertTrigger) => {
        if (showLocked) return onUnlock();
        if (disabled) return;
        setMessage(null);
        setOn((cur) => (cur.includes(key) ? cur.filter((k) => k !== key) : [...cur, key]));
    };

    const save = async () => {
        if (showLocked) return onUnlock();
        if (!usable || saving) return;
        setSaving(true);
        setMessage(null);
        try {
            if (!on.length) {
                if (sub) {
                    const res = await deleteDivAlert(sub.id);
                    if (res.kind === "ok") {
                        setSub(null);
                        setMessage("Alert removed.");
                    } else setMessage(res.kind === "error" ? res.message : "Could not remove this alert.");
                }
                return;
            }
            const triggers = [...on, ...kept];
            const input = { tradingitem_id: tradingitemId, triggers, channel, params: ALERT_PARAMS };
            const res = sub ? await updateDivAlert(sub.id, { triggers, channel, params: ALERT_PARAMS }) : await createDivAlert(input);
            if (res.kind === "ok") {
                setSub(res.data);
                // On the Alerts page itself, pointing at the Alerts page reads oddly.
                setMessage(onAlertsPage ? "Saved." : "Saved. Manage it on the Alerts page.");
            } else if (res.kind === "upgrade") setStatus("upgrade");
            else if (res.kind === "missing") {
                setStatus("missing");
                onUnavailable();
            } else setMessage(res.message);
        } finally {
            setSaving(false);
        }
    };

    if (status === "missing") return null;

    return (
        <Box
            title="Alert me"
            label={`Dividend alerts for ${ticker}`}
            tour="div-alert"
            help="Notifications for this stock: when a dividend is declared and how it compares with the forecast, when the forecast moves, when a declaration is late, before an ex-date, or when a special dividend is likely."
        >
            <div className={styles.switches} role="group" aria-label="Alert triggers">
                {switches.map((s) => {
                    const checked = !showLocked && on.includes(s.key);
                    return (
                        <button key={s.key} type="button" role="switch" aria-checked={checked} aria-disabled={disabled} className={styles.switch} onClick={() => toggle(s.key)}>
                            <i aria-hidden="true" />
                            {s.label}
                        </button>
                    );
                })}
            </div>
            <div className={styles.alertRow}>
                <DeliverySelect ariaLabel="Delivery" value={channel} disabled={disabled || showLocked} onChange={(value) => setChannel(value)} />
                <button type="button" className={styles.saveButton} disabled={!showLocked && (disabled || !dirty)} onClick={save}>
                    {showLocked ? "See plans" : saving ? "Saving…" : "Save"}
                </button>
            </div>
            {showLocked && <div className={styles.alertNote}>Dividend alerts are part of ORTEX Advanced.</div>}
            {!showLocked && demo && <div className={styles.alertNote}>Alerts work on live data, not on this example.</div>}
            {!showLocked && status === "error" && <div className={styles.alertNote}>Alerts are not reachable right now.</div>}
            {message && (
                <div className={message.startsWith("Saved") || message === "Alert removed." ? styles.alertNote : styles.alertError} role="status">
                    {message}
                </div>
            )}
        </Box>
    );
};

export default DividendAlertBox;
